import { useNavigate, useLocation } from 'react-router-dom';
import { useMemo } from 'react';

import DsIcon from './DsIcon';
import { useAuthStore } from '@/store/auth.store';
import { useDeploymentStore } from '@/store/deployment.store';
import { getWebchatUrl } from '@/config';
import { isSidebarMenuVisible } from '@/config/deployment/sidebar-menu';

interface SidebarProps {
  collapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
  accessControl: boolean;
  onExitAccessControl: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: string;
  /** 无 path 表示暂未接入页面的占位项（下一任务再补路由） */
  path?: string;
}

interface NavGroup {
  title?: string;
  items: NavItem[];
}

/** 访问控制是账户菜单下的独立上下文，不受平台左侧导航配置影响。 */
const ACCESS_CONTROL_NAV_GROUPS: NavGroup[] = [
  {
    title: '身份管理',
    items: [
      { id: 'iam-users', label: '用户管理', icon: 'users', path: '/iam/users' },
      { id: 'iam-groups', label: '用户组管理', icon: 'folder-key', path: '/iam/groups' },
    ],
  },
  {
    title: '权限管理',
    items: [
      { id: 'iam-policies', label: '权限策略', icon: 'shield-check', path: '/iam/policies' },
      { id: 'iam-grants', label: '授权', icon: 'badge-check', path: '/iam/grants' },
    ],
  },
  {
    title: '登录集成',
    items: [{ id: 'iam-sso', label: 'SSO 集成', icon: 'key-round', path: '/iam/sso' }],
  },
];

/** 图标注册表：key -> lucide kebab name */
const ICON_MAP: Record<string, string> = {
  dashboard: 'layout-dashboard',
  agents: 'agent-template',
  applications: 'bot',
  'skills-plaza': 'store',
  channels: 'radio',
  'channel-accounts': 'plug',
  credentials: 'key-round',
  sandboxes: 'package',
  mcps: 'cable',
  memories: 'brain',
  'rags-build': 'database',
  'rags-catalog': 'library',
  skillgroups: 'puzzle',
  'skills-management': 'wrench',
  models: 'cpu',
  webchat: 'messages-square',
};

const iconFor = (key: string) => ICON_MAP[key] ?? 'circle';

/** 内置静态导航 */
const FALLBACK_NAV_GROUPS: NavGroup[] = [
  {
    items: [{ id: 'dashboard', label: '工作台', icon: iconFor('dashboard'), path: '/dashboard' }],
  },
  {
    title: '智能体中心',
    items: [
      { id: 'agents', label: '智能体模板', icon: iconFor('agents'), path: '/agents' },
      {
        id: 'applications',
        label: '智能体实例',
        icon: iconFor('applications'),
        path: '/applications',
      },
    ],
  },
  {
    title: '渠道接入',
    items: [
      { id: 'channels', label: '渠道', icon: iconFor('channels'), path: '/channels' },
      {
        id: 'channel-accounts',
        label: '渠道账号',
        icon: iconFor('channel-accounts'),
        path: '/channel-accounts',
      },
    ],
  },
  {
    title: '凭证管理',
    items: [
      { id: 'credentials', label: '凭证管理', icon: iconFor('credentials'), path: '/credentials' },
    ],
  },
  {
    title: '基础组件',
    items: [
      { id: 'sandboxes', label: '沙箱', icon: iconFor('sandboxes'), path: '/plugins/sandboxes' },
      { id: 'mcps', label: 'MCP', icon: iconFor('mcps'), path: '/plugins/mcps' },
      { id: 'memories', label: '记忆库', icon: iconFor('memories'), path: '/plugins/memories' },
      { id: 'models', label: '模型', icon: iconFor('models'), path: '/plugins/models' },
    ],
  },
  {
    title: '知识库',
    items: [
      {
        id: 'rags-build',
        label: '知识库管理',
        icon: iconFor('rags-build'),
        path: '/plugins/rags/build',
      },
      {
        id: 'rags-catalog',
        label: '知识库组',
        icon: iconFor('rags-catalog'),
        path: '/plugins/rags/catalog',
      },
    ],
  },
  {
    title: 'Skills',
    items: [
      { id: 'skillgroups', label: '技能组', icon: iconFor('skillgroups'), path: '/plugins/skills' },
      {
        id: 'skills-management',
        label: 'Skill 管理',
        icon: iconFor('skills-management'),
        path: '/plugins/skills/management',
      },
      {
        id: 'skills-plaza',
        label: 'Skills 广场',
        icon: iconFor('skills-plaza'),
        path: '/plugins/skills/plaza',
      },
    ],
  },
];

export default function Sidebar({
  collapsed,
  onCollapseChange,
  accessControl,
  onExitAccessControl,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const deploymentProfile = useDeploymentStore((s) => s.profile);
  const canViewSsoConfig = useDeploymentStore((s) => s.hasCapability('iam.ssoConfig'));

  const webchatUrl = getWebchatUrl();
  const webchatEnabled = !!webchatUrl && !!token;

  // 租户级导航配置已下线，直接使用内置静态导航。
  const navGroups = FALLBACK_NAV_GROUPS;

  const isActive = (item: NavItem) => {
    const path = item.path;
    if (!path) return false;
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    if (path === '/agents' && /^\/agents\/[^/]+\/apps/.test(location.pathname)) {
      return false;
    }
    if (path === '/applications' && /^\/agents\/[^/]+\/apps/.test(location.pathname)) {
      return true;
    }
    if (item.id === 'skillgroups' && path === '/plugins/skills') {
      return (
        location.pathname === '/plugins/skills' ||
        (location.pathname.startsWith('/plugins/skills/') &&
          !location.pathname.startsWith('/plugins/skills/management') &&
          !location.pathname.startsWith('/plugins/skills/plaza'))
      );
    }
    if (item.id === 'rags-build') {
      return (
        location.pathname === '/plugins/rags/build' ||
        location.pathname === '/plugins/rags' ||
        location.pathname.startsWith('/plugins/rags/build/')
      );
    }
    if (item.id === 'rags-catalog') {
      return (
        location.pathname === '/plugins/rags/catalog' ||
        location.pathname.startsWith('/plugins/rags/catalog/') ||
        location.pathname.startsWith('/plugins/rags/group-classes/') ||
        location.pathname.startsWith('/plugins/rags/groups/')
      );
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const openWebchat = () => {
    if (!webchatUrl || !token) return;
    const u = new URL(webchatUrl, window.location.origin);
    u.searchParams.set('token', token);
    u.searchParams.set('expires_sec', '3600');
    window.open(u.toString(), '_blank', 'noopener,noreferrer');
  };

  const groups = useMemo(() => {
    if (!accessControl) return filterNavGroups(navGroups, deploymentProfile);
    console.log('canViewSsoConfig', canViewSsoConfig)
    if (canViewSsoConfig) return ACCESS_CONTROL_NAV_GROUPS;
    return ACCESS_CONTROL_NAV_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => item.id !== 'iam-sso')
    })).filter(group => group.items.length > 0);
  }, [accessControl, canViewSsoConfig, deploymentProfile, navGroups]);

  const handleItemClick = (item: NavItem) => {
    // 无 path 的占位项暂不接入页面（下一任务补路由）。
    if (item.path) navigate(item.path);
  };

  return (
    <aside className={`sidebar ${accessControl ? 'is-access-control' : ''}`}>
      {accessControl && (
        <div className="sidebar-context">
          <button
            type="button"
            className="sidebar-context-back"
            title="返回平台"
            aria-label="返回平台"
            onClick={onExitAccessControl}
          >
            <DsIcon name="arrow-left" size={15} />
          </button>
          {!collapsed && (
            <div className="sidebar-context-copy">
              <strong>访问控制</strong>
              <span>用户与权限管理</span>
            </div>
          )}
        </div>
      )}
      <nav className="sidebar-nav">
        {groups.map((group, gi) => (
          <div key={gi}>
            {group.title && !collapsed && <div className="nav-group-title">{group.title}</div>}
            {group.items.map((item) => (
              <div
                key={item.id}
                className={`nav-item ${isActive(item) ? 'is-active' : ''} ${
                  item.path ? '' : 'is-disabled'
                }`}
                onClick={() => handleItemClick(item)}
                title={collapsed ? item.label : item.path ? undefined : '敬请期待'}
              >
                <div className="nav-item-inner">
                  <DsIcon name={item.icon} size={16} />
                  <span className="nav-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
        {webchatEnabled && isSidebarMenuVisible(deploymentProfile, 'webchat') && (
          <div>
            {!collapsed && <div className="nav-group-title">对话</div>}
            <div
              className="nav-item"
              onClick={openWebchat}
              title={collapsed ? '网页版对话' : undefined}
            >
              <div className="nav-item-inner">
                <DsIcon name={iconFor('webchat')} size={16} />
                <span className="nav-label">网页版对话</span>
              </div>
            </div>
          </div>
        )}
      </nav>

      <div className="sidebar-spacer" />

      <div className="collapse-bar">
        <button
          className="collapse-btn"
          type="button"
          title={collapsed ? '展开' : '收起'}
          onClick={() => onCollapseChange(!collapsed)}
        >
          <DsIcon name="menu" size={18} />
        </button>
        {!collapsed && <span className="collapse-hint">Platform · v0</span>}
      </div>
    </aside>
  );
}

function filterNavGroups(groups: NavGroup[], profile: ReturnType<typeof useDeploymentStore.getState>['profile']) {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isSidebarMenuVisible(profile, item.id)),
    }))
    .filter((group) => group.items.length > 0);
}
